// ═══════════════════════════════════════════════════════════════
// DonorDetailView — Classification Section Components
// Path: src/components/donorDetail/ClassificationSection.jsx
//
// Extracted from DonorDetailView.jsx during modularization Phase 2.
// Contains all classification, flag, committee, and contact preference
// controls used by both 2-Column sidebar and Classic layout.
//
// Exports:
//   ClassificationControls  — pathway, persona, flags, manager, committees
//   FlagsCompact            — checkbox flags for sidebar
//   CommitteeCheckboxes     — committee multi-select for sidebar
//   ContactPreferencesCompact — opt-out toggles for sidebar + classic
//
// All components receive data and handlers via props.
// No component in this file calls useApp() directly.
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { AlertCircle, Info, ShieldAlert } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { FormSelect } from "../ui/FormControls";
import { COMMITTEES } from "../../constants/committees";
import { DONOR_TYPES } from "../../constants/memberTypes";

// ═══════════════════════════════════════════════════════════════
// CONTACT PREFERENCE FLAGS (v1.3.0)
// ═══════════════════════════════════════════════════════════════

const PREF_FLAGS = [
  { field: "noEmail", label: "No Email", channel: "email" },
  { field: "noMail", label: "No Mail", channel: "postal mail" },
  { field: "noPhone", label: "No Phone", channel: "phone calls" },
  { field: "noText", label: "No Text", channel: "text messages" },
  { field: "noSolicitation", label: "Do Not Solicit", channel: "all solicitations" },
  { field: "badAddress", label: "Bad Address", channel: "postal mail (bad address)" },
  { field: "isDeceased", label: "Deceased", channel: "all contact (deceased)" },
];

// ═══════════════════════════════════════════════════════════════
// ContactPreferencesCompact
// ═══════════════════════════════════════════════════════════════

export const ContactPreferencesCompact = ({
  detailForm,
  canEdit,
  updateField,
  updateMember,
  selectedMember,
  palette,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <div
      style={{
        fontSize: FONT_SIZES.xs,
        fontWeight: 700,
        color: palette.text3,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: 2,
      }}
    >
      Contact Preferences
    </div>
    {PREF_FLAGS.map((pf) => {
      const isActive = detailForm[pf.field] || false;
      return (
        <label
          key={pf.field}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: FONT_SIZES.sm,
            color: isActive ? "#b45309" : palette.text1,
            cursor: canEdit ? "pointer" : "default",
            opacity: canEdit ? 1 : 0.6,
          }}
        >
          <input
            type="checkbox"
            checked={isActive}
            disabled={!canEdit}
            onChange={(e) => {
              const checked = e.target.checked;
              updateField(pf.field, checked);
              const updates = { [pf.field]: checked };
              // Auto-set deceased date when marking as deceased
              if (pf.field === "isDeceased") {
                updates.deceasedDate = checked ? new Date().toISOString() : null;
              }
              updateMember(selectedMember.id, updates, true);
            }}
            style={{ accentColor: pf.field === "isDeceased" ? "#374151" : "#d97706" }}
          />
          <span style={{ fontWeight: isActive ? 700 : 400 }}>{pf.label}</span>
          {isActive && (
            <ShieldAlert
              size={ICON_SIZES.xs}
              style={{ color: "#d97706", marginLeft: "auto" }}
            />
          )}
        </label>
      );
    })}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// FlagsCompact — checkbox flags for sidebar
// ═══════════════════════════════════════════════════════════════

export const FlagsCompact = ({
  detailForm,
  canEdit,
  updateField,
  updateMember,
  selectedMember,
  effective,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    {[
      { field: "isDonor", label: "Donor" },
      { field: "isBoardMember", label: "Board" },
      { field: "isRecurring", label: "Recurring" },
    ].map((flag) => (
      <label
        key={flag.field}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: FONT_SIZES.sm,
          color: "#1f2937",
          cursor: canEdit ? "pointer" : "default",
          opacity: canEdit ? 1 : 0.6,
        }}
      >
        <input
          type="checkbox"
          checked={detailForm[flag.field] || false}
          disabled={!canEdit}
          onChange={(e) => {
            updateField(flag.field, e.target.checked);
            updateMember(
              selectedMember.id,
              { [flag.field]: e.target.checked },
              true
            );
          }}
          style={{ accentColor: effective.color }}
        />
        {flag.label}
      </label>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// CommitteeCheckboxes — committee multi-select for sidebar
// ═══════════════════════════════════════════════════════════════

export const CommitteeCheckboxes = ({
  detailForm,
  canEdit,
  updateField,
  updateMember,
  selectedMember,
  effective,
  palette,
}) => {
  const toggleCommittee = (val) => {
    if (!canEdit) return;
    const current = detailForm.committee || [];
    const updated = current.includes(val)
      ? current.filter((c) => c !== val)
      : [...current, val];
    updateField("committee", updated);
    updateMember(selectedMember.id, { committee: updated }, true);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          fontSize: FONT_SIZES.xs,
          fontWeight: 700,
          color: palette.text3,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          marginBottom: 2,
        }}
      >
        Committees
      </div>
      {COMMITTEES.map((c) => (
        <label
          key={c.value}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: FONT_SIZES.sm,
            color: palette.text1,
            cursor: canEdit ? "pointer" : "default",
            opacity: canEdit ? 1 : 0.6,
          }}
        >
          <input
            type="checkbox"
            checked={(detailForm.committee || []).includes(c.value)}
            disabled={!canEdit}
            onChange={() => toggleCommittee(c.value)}
            style={{ accentColor: effective.color }}
          />
          {c.label}
        </label>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// PrimaryContactField — free text with CRM member autocomplete
// ═══════════════════════════════════════════════════════════════

const PrimaryContactField = ({
  detailForm,
  canEdit,
  updateField,
  updateMember,
  selectedMember,
  members,
  palette,
}) => {
  const [search, setSearch] = useState(
    detailForm.primaryContactName || ""
  );
  const [showDropdown, setShowDropdown] = useState(false);

  // Sync from detailForm when member changes
  React.useEffect(() => {
    // If there's a linked contact, show their name; otherwise show the free text
    if (detailForm.primaryContactId && members) {
      const linked = members.find((m) => m.id === detailForm.primaryContactId);
      if (linked) {
        setSearch(
          [linked.firstName, linked.lastName].filter(Boolean).join(" ") || ""
        );
        return;
      }
    }
    setSearch(detailForm.primaryContactName || "");
  }, [detailForm.primaryContactId, detailForm.primaryContactName, members]);

  const matches = React.useMemo(() => {
    if (!search || search.trim().length < 2) return [];
    const query = search.toLowerCase();
    return (members || [])
      .filter(
        (m) =>
          m.id !== selectedMember?.id &&
          m.donorType !== "organization" &&
          `${m.firstName} ${m.lastName}`
            .toLowerCase()
            .includes(query)
      )
      .slice(0, 8);
  }, [search, members, selectedMember]);

  const handleSelectMember = (m) => {
    const name = [m.firstName, m.lastName].filter(Boolean).join(" ");
    setSearch(name);
    setShowDropdown(false);
    updateField("primaryContactId", m.id);
    updateField("primaryContactName", name);
    updateMember(
      selectedMember.id,
      { primaryContactId: m.id, primaryContactName: name },
      true
    );
  };

  const handleBlur = () => {
    // Delay to allow dropdown click to register
    setTimeout(() => {
      setShowDropdown(false);
      // Save as free text if no linked member selected
      if (search !== (detailForm.primaryContactName || "")) {
        updateField("primaryContactName", search);
        updateField("primaryContactId", null);
        updateMember(
          selectedMember.id,
          { primaryContactName: search, primaryContactId: null },
          true
        );
      }
    }, 200);
  };

  const linkedMember = detailForm.primaryContactId
    ? (members || []).find((m) => m.id === detailForm.primaryContactId)
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, position: "relative" }}>
      <label
        style={{
          fontSize: FONT_SIZES.xs,
          fontWeight: 600,
          color: palette.text2,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        Primary Contact
      </label>
      <input
        value={search}
        onChange={(e) => {
          if (!canEdit) return;
          setSearch(e.target.value);
          setShowDropdown(true);
          // Clear linked member if user is typing
          if (detailForm.primaryContactId) {
            updateField("primaryContactId", null);
          }
        }}
        onFocus={() => {
          if (search.trim().length >= 2) setShowDropdown(true);
        }}
        onBlur={handleBlur}
        placeholder="Type a name..."
        disabled={!canEdit}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          fontSize: FONT_SIZES.sm,
          fontFamily: FONT_FAMILY,
          border: `1px solid ${linkedMember ? "#10b981" : palette.inputBorder}`,
          background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
          color: palette.inputColor,
          outline: "none",
          opacity: canEdit ? 1 : 0.6,
        }}
      />
      {linkedMember && (
        <div style={{ fontSize: "0.6rem", color: "#10b981", fontWeight: 600, fontFamily: FONT_FAMILY }}>
          Linked to CRM record
        </div>
      )}
      {showDropdown && matches.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 20,
            marginTop: 2,
            background: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            maxHeight: 160,
            overflowY: "auto",
          }}
        >
          {matches.map((m) => (
            <div
              key={m.id}
              onMouseDown={() => handleSelectMember(m)}
              style={{
                padding: "6px 10px",
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                color: palette.text1,
                cursor: "pointer",
                borderBottom: `1px solid ${palette.cardBorder}`,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = `${palette.accent1}08`)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <strong>
                {m.lastName && m.firstName
                  ? `${m.lastName}, ${m.firstName}`
                  : m.lastName || m.firstName || "Unknown"}
              </strong>
              {m.organization && (
                <span style={{ color: palette.text3, marginLeft: 6 }}>
                  {m.organization}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ClassificationControls — pathway, persona, flags, manager,
//   committees, mismatch indicator
//
// Props:
//   compact (boolean) — true for sidebar, false for classic layout
//     When compact: hides flags and committee sections (those render
//     separately via FlagsCompact and CommitteeCheckboxes)
//     When !compact: includes flags and committees inline
// ═══════════════════════════════════════════════════════════════

export const ClassificationControls = ({
  detailForm,
  canEdit,
  updateField,
  updateMember,
  selectedMember,
  pathways,
  personas,
  directors,
  members,
  effective,
  palette,
  compact = false,
}) => {
  // Persona help panel — local state (only used here)
  const [showPersonaHelp, setShowPersonaHelp] = useState(false);

  const toggleCommittee = (val) => {
    if (!canEdit) return;
    const current = detailForm.committee || [];
    const updated = current.includes(val)
      ? current.filter((c) => c !== val)
      : [...current, val];
    updateField("committee", updated);
    updateMember(selectedMember.id, { committee: updated }, true);
  };

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "1fr" : "1fr 1fr",
          gap: compact ? 12 : 16,
        }}
      >
        {/* Donor Type */}
        <FormSelect
          label="Donor Type"
          value={detailForm.donorType || "individual"}
          onChange={(v) => {
            if (!canEdit) return;
            updateField("donorType", v);
            updateMember(selectedMember.id, { donorType: v }, true);
          }}
          options={DONOR_TYPES}
          disabled={!canEdit}
        />

        {/* Household fields — only for households */}
        {(detailForm.donorType === "household") && (
          <>
            {/* Household Name */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: compact ? "1" : "1 / -1" }}>
              <label
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  color: palette.text2,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Household Name (for receipts)
              </label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  value={detailForm.householdName || ""}
                  onChange={(e) => {
                    if (!canEdit) return;
                    updateField("householdName", e.target.value);
                  }}
                  onBlur={() => {
                    if (!canEdit) return;
                    updateMember(
                      selectedMember.id,
                      { householdName: detailForm.householdName || "" },
                      true
                    );
                  }}
                  placeholder="e.g., Jerry & Sharon Melson"
                  disabled={!canEdit}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: FONT_SIZES.sm,
                    fontFamily: FONT_FAMILY,
                    border: `1px solid ${palette.inputBorder}`,
                    background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
                    color: palette.inputColor,
                    outline: "none",
                    opacity: canEdit ? 1 : 0.6,
                  }}
                />
                {canEdit && detailForm.spouseFirstName && !detailForm.householdName && (
                  <button
                    onClick={() => {
                      const suggested = `${detailForm.firstName || ""} & ${detailForm.spouseFirstName || ""} ${detailForm.lastName || ""}`.trim();
                      updateField("householdName", suggested);
                      updateMember(selectedMember.id, { householdName: suggested }, true);
                    }}
                    title="Auto-fill from names"
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: `1px solid ${palette.accent1}30`,
                      background: `${palette.accent1}08`,
                      color: palette.accent1,
                      fontSize: FONT_SIZES.xs,
                      fontFamily: FONT_FAMILY,
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Auto-fill
                  </button>
                )}
              </div>
            </div>

            {/* Spouse section header */}
            <div style={{ gridColumn: compact ? "1" : "1 / -1", marginTop: 4 }}>
              <div
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 700,
                  color: palette.text3,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  paddingBottom: 4,
                  borderBottom: `1px solid ${palette.cardBorder}`,
                }}
              >
                Spouse Information
              </div>
            </div>

            {/* Spouse First Name */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Spouse First Name
              </label>
              <input
                value={detailForm.spouseFirstName || ""}
                onChange={(e) => { if (!canEdit) return; updateField("spouseFirstName", e.target.value); }}
                onBlur={() => { if (!canEdit) return; updateMember(selectedMember.id, { spouseFirstName: detailForm.spouseFirstName || "" }, true); }}
                placeholder="First name"
                disabled={!canEdit}
                style={{
                  padding: "8px 12px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
                  color: palette.inputColor, outline: "none", opacity: canEdit ? 1 : 0.6,
                }}
              />
            </div>

            {/* Spouse Last Name */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Spouse Last Name
              </label>
              <input
                value={detailForm.spouseLastName || ""}
                onChange={(e) => { if (!canEdit) return; updateField("spouseLastName", e.target.value); }}
                onBlur={() => { if (!canEdit) return; updateMember(selectedMember.id, { spouseLastName: detailForm.spouseLastName || "" }, true); }}
                placeholder="Last name"
                disabled={!canEdit}
                style={{
                  padding: "8px 12px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
                  color: palette.inputColor, outline: "none", opacity: canEdit ? 1 : 0.6,
                }}
              />
            </div>

            {/* Spouse Email */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Spouse Email
              </label>
              <input
                type="email"
                value={detailForm.spouseEmail || ""}
                onChange={(e) => { if (!canEdit) return; updateField("spouseEmail", e.target.value); }}
                onBlur={() => { if (!canEdit) return; updateMember(selectedMember.id, { spouseEmail: detailForm.spouseEmail || "" }, true); }}
                placeholder="email@example.com"
                disabled={!canEdit}
                style={{
                  padding: "8px 12px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
                  color: palette.inputColor, outline: "none", opacity: canEdit ? 1 : 0.6,
                }}
              />
            </div>

            {/* Spouse Phone */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Spouse Phone
              </label>
              <input
                type="tel"
                value={detailForm.spousePhone || ""}
                onChange={(e) => { if (!canEdit) return; updateField("spousePhone", e.target.value); }}
                onBlur={() => { if (!canEdit) return; updateMember(selectedMember.id, { spousePhone: detailForm.spousePhone || "" }, true); }}
                placeholder="(555) 555-5555"
                disabled={!canEdit}
                style={{
                  padding: "8px 12px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
                  color: palette.inputColor, outline: "none", opacity: canEdit ? 1 : 0.6,
                }}
              />
            </div>
          </>
        )}

        {/* Primary Contact — only for organizations */}
        {(detailForm.donorType === "organization") && (
          <PrimaryContactField
            detailForm={detailForm}
            canEdit={canEdit}
            updateField={updateField}
            updateMember={updateMember}
            selectedMember={selectedMember}
            members={members}
            palette={palette}
          />
        )}

        {/* Status */}
        <FormSelect
          label="Status"
          value={detailForm.status || "active"}
          onChange={(v) => {
            if (!canEdit) return;
            updateField("status", v);
            const updates = { status: v };
            if (v === "lapsed" || v === "suspended") {
              updates.lapsed_date = new Date().toISOString();
            } else {
              updates.lapsed_date = null;
            }
            updateMember(selectedMember.id, updates, true);
          }}
          options={[
            { value: "active", label: "Active" },
            { value: "lapsed", label: "Lapsed" },
            { value: "suspended", label: "Suspended" },
          ]}
          disabled={!canEdit}
        />

        {/* Pathway */}
        <FormSelect
          label="Pathway"
          value={detailForm.pathway}
          onChange={(v) => {
            if (!canEdit) return;
            updateField("pathway", v);
            updateMember(selectedMember.id, { pathway: v }, true);
          }}
          options={pathways.map((p) => ({ value: p.id, label: p.name }))}
          disabled={!canEdit}
        />

        {/* Persona */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                color: palette.text2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Persona
            </label>
            {(personas || []).length > 0 && (
              <button
                onClick={() => setShowPersonaHelp(!showPersonaHelp)}
                title="What are personas?"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  border: "none",
                  background: showPersonaHelp
                    ? palette.accent1
                    : `${palette.accent1}15`,
                  color: showPersonaHelp ? "#fff" : palette.accent1,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  padding: 0,
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  if (!showPersonaHelp)
                    e.currentTarget.style.background = `${palette.accent1}30`;
                }}
                onMouseLeave={(e) => {
                  if (!showPersonaHelp)
                    e.currentTarget.style.background = `${palette.accent1}15`;
                }}
              >
                <Info size={ICON_SIZES.xs} />
              </button>
            )}
          </div>
          <select
            value={detailForm.persona || ""}
            onChange={(e) => {
              if (!canEdit) return;
              const val = e.target.value || null;
              updateField("persona", val);
              updateMember(selectedMember.id, { persona: val }, true);
            }}
            disabled={!canEdit}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
              border: `1px solid ${palette.inputBorder}`,
              background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
              color: palette.inputColor,
              outline: "none",
              opacity: canEdit ? 1 : 0.6,
              cursor: canEdit ? "pointer" : "default",
            }}
          >
            <option value="">(None)</option>
            {(personas || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {showPersonaHelp && (personas || []).length > 0 && (
            <div
              style={{
                marginTop: 6,
                padding: "10px 12px",
                borderRadius: 8,
                background: "#ffffff",
                border: `1px solid ${palette.cardBorder}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: palette.text2,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: 8,
                }}
              >
                Available Personas
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(personas || []).map((p) => (
                  <div
                    key={p.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      padding: "6px 8px",
                      borderRadius: 6,
                      background: `${p.color || "#6b7280"}06`,
                      border: `1px solid ${p.color || "#6b7280"}15`,
                      cursor: canEdit ? "pointer" : "default",
                      transition: "background 0.12s",
                    }}
                    onClick={() => {
                      if (!canEdit) return;
                      updateField("persona", p.id);
                      updateMember(selectedMember.id, { persona: p.id }, true);
                      setShowPersonaHelp(false);
                    }}
                    onMouseEnter={(e) => {
                      if (canEdit)
                        e.currentTarget.style.background = `${
                          p.color || "#6b7280"
                        }12`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `${
                        p.color || "#6b7280"
                      }06`;
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: p.color || "#6b7280",
                        flexShrink: 0,
                        marginTop: 3,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: FONT_SIZES.xs,
                          fontWeight: 700,
                          color: palette.text1,
                          lineHeight: 1.3,
                        }}
                      >
                        {p.name}
                        {detailForm.persona === p.id && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 9,
                              fontWeight: 600,
                              color: "#fff",
                              background: p.color || "#6b7280",
                              padding: "1px 5px",
                              borderRadius: 3,
                              verticalAlign: "middle",
                            }}
                          >
                            Selected
                          </span>
                        )}
                      </div>
                      {p.description && (
                        <div
                          style={{
                            fontSize: 11,
                            color: palette.text2,
                            lineHeight: 1.4,
                            marginTop: 2,
                          }}
                        >
                          {p.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Flags (classic only) */}
        {!compact && (
          <div>
            <label
              style={{
                fontSize: FONT_SIZES.sm,
                fontWeight: 600,
                color: palette.text2,
                display: "block",
                marginBottom: 4,
              }}
            >
              Flags
            </label>
            <div style={{ display: "flex", gap: 14, marginTop: 6 }}>
              {[
                { field: "isDonor", label: "Donor" },
                { field: "isBoardMember", label: "Board" },
              ].map((flag) => (
                <label
                  key={flag.field}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: FONT_SIZES.sm,
                    cursor: canEdit ? "pointer" : "default",
                    opacity: canEdit ? 1 : 0.6,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={detailForm[flag.field] || false}
                    disabled={!canEdit}
                    onChange={(e) => {
                      updateField(flag.field, e.target.checked);
                      updateMember(
                        selectedMember.id,
                        { [flag.field]: e.target.checked },
                        true
                      );
                    }}
                  />
                  {flag.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Manager */}
        <FormSelect
          label="Assigned Director"
          value={detailForm.assignedDirector || ""}
          onChange={(v) => {
            if (!canEdit) return;
            updateField("assignedDirector", v);
            updateMember(selectedMember.id, { assignedDirector: v }, true);
          }}
          options={[
            { value: "", label: "Unassigned" },
            ...(directors || []).map((d) => ({
              value: d.name,
              label: d.name,
            })),
          ]}
          disabled={!canEdit}
        />
      </div>

      {/* Public Recognition — donor wall / program list controls */}
      <div
        style={{
          marginTop: 14,
          paddingTop: 14,
          borderTop: `1px solid ${palette.cardBorder}`,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            color: palette.text3,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Public Recognition
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              color: palette.text2,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Publication Name (donor wall)
          </label>
          <input
            value={detailForm.publicationName || ""}
            onChange={(e) => {
              if (!canEdit) return;
              updateField("publicationName", e.target.value);
            }}
            onBlur={() => {
              if (!canEdit) return;
              updateMember(
                selectedMember.id,
                { publicationName: detailForm.publicationName || "" },
                true
              );
            }}
            placeholder='e.g., "Mr. and Mrs. John Smith" or "The Smith Family"'
            disabled={!canEdit || detailForm.isAnonymous}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
              border: `1px solid ${palette.inputBorder}`,
              background: canEdit && !detailForm.isAnonymous
                ? palette.inputBg
                : `${palette.inputBg}80`,
              color: palette.inputColor,
              outline: "none",
              opacity: canEdit && !detailForm.isAnonymous ? 1 : 0.6,
            }}
          />
          <div
            style={{
              fontSize: 11,
              color: palette.text3,
              lineHeight: 1.4,
              marginTop: 2,
            }}
          >
            How this donor's name appears on programs and the donor wall.
            Leave blank to use the household or legal name.
          </div>
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: FONT_SIZES.sm,
            color: detailForm.isAnonymous ? "#b45309" : palette.text1,
            cursor: canEdit ? "pointer" : "default",
            opacity: canEdit ? 1 : 0.6,
            padding: "6px 0",
          }}
        >
          <input
            type="checkbox"
            checked={detailForm.isAnonymous || false}
            disabled={!canEdit}
            onChange={(e) => {
              const checked = e.target.checked;
              updateField("isAnonymous", checked);
              updateMember(
                selectedMember.id,
                { isAnonymous: checked },
                true
              );
            }}
            style={{ accentColor: "#d97706" }}
          />
          <span style={{ fontWeight: detailForm.isAnonymous ? 700 : 400 }}>
            Anonymous — do not list on donor walls or programs
          </span>
        </label>
      </div>

      {/* Mismatch indicator */}
      {effective.mismatch && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 14,
            padding: "10px 14px",
            borderRadius: 8,
            background: "#fffbeb",
            border: "1px solid #fcd34d",
          }}
        >
          <AlertCircle size={ICON_SIZES.sm} style={{ color: "#d97706", flexShrink: 0 }} />
          <span
            style={{
              fontSize: FONT_SIZES.xs,
              color: "#92400e",
              lineHeight: 1.4,
            }}
          >
            Director set pathway to <strong>{effective.label}</strong> —
            engagement score suggests{" "}
            <strong>{effective.calculatedLabel}</strong>.
            {effective.source === "override" &&
              " The manual override is controlling the card color."}
          </span>
        </div>
      )}
    </>
  );
};
